import { useState, useEffect, useCallback, useRef } from 'react';
import { SyncEvent } from '../api/generated/model';

/**
 * Hook to track and manage sync events received via SSE
 *
 * This hook maintains a map of active syncs and provides callbacks
 * for sync lifecycle events (started, progress, completed, failed)
 */

export interface UseSyncEventsOptions {
  /** Callback fired when a sync starts */
  onSyncStarted?: (event: SyncEvent) => void;
  /** Callback fired on sync progress updates */
  onSyncProgress?: (event: SyncEvent) => void;
  /** Callback fired when a sync completes successfully */
  onSyncCompleted?: (event: SyncEvent) => void;
  /** Callback fired when a sync fails */
  onSyncFailed?: (event: SyncEvent) => void;
  /** Auto-cleanup completed syncs after this many milliseconds (default: 5000) */
  cleanupDelayMs?: number;
}

export interface UseSyncEventsReturn {
  /** Map of sync_id -> SyncEvent for all active/recent syncs */
  activeSyncs: Map<string, SyncEvent>;
  /** Register a sync event handler (called by useNotificationStream) */
  handleSyncEvent: (event: SyncEvent) => void;
  /** Get sync state for a specific sync_id */
  getSyncState: (syncId: string) => SyncEvent | undefined;
  /** Check if a sync is currently active */
  isSyncing: (syncId?: string) => boolean;
}

export function useSyncEvents(options: UseSyncEventsOptions = {}): UseSyncEventsReturn {
  const {
    onSyncStarted,
    onSyncProgress,
    onSyncCompleted,
    onSyncFailed,
    cleanupDelayMs = 5000,
  } = options;

  const [activeSyncs, setActiveSyncs] = useState<Map<string, SyncEvent>>(new Map());
  const cleanupTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup function for a specific sync
  const scheduleCleanup = useCallback((syncId: string) => {
    // Clear any existing timer
    const existingTimer = cleanupTimersRef.current.get(syncId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Schedule new cleanup
    const timer = setTimeout(() => {
      setActiveSyncs(prev => {
        const next = new Map(prev);
        next.delete(syncId);
        return next;
      });
      cleanupTimersRef.current.delete(syncId);
    }, cleanupDelayMs);

    cleanupTimersRef.current.set(syncId, timer);
  }, [cleanupDelayMs]);

  // Handle incoming sync events
  const handleSyncEvent = useCallback((event: SyncEvent) => {
    if (!event.sync_id) {
      console.warn('Received sync event without sync_id:', event);
      return;
    }

    // Debug logging for sync events
    console.log(`[useSyncEvents] Processing ${event.status} event for ${event.integration_id}:`, {
      syncId: event.sync_id,
      status: event.status,
      message: event.progress?.message,
      timestamp: event.timestamp
    });

    // Update active syncs - accumulate messages for batched updates
    setActiveSyncs(prev => {
      const next = new Map(prev);
      const existing = prev.get(event.sync_id);

      // If this is a progress event and we already have messages, accumulate them
      if (event.status === 'progress' && existing && (existing.status === 'started' || existing.status === 'progress')) {
        const existingMessages: string[] = (existing as any).messages || (existing.progress?.message ? [existing.progress.message] : []);
        const existingCurrent = existing.progress?.current;
        const newCurrent = event.progress?.current;
        const newMessage = event.progress?.message;

        let messages: string[];
        if (newMessage) {
          // Same progress step — replace the last message; new step — append
          if (newCurrent !== undefined && newCurrent === existingCurrent) {
            messages = [...existingMessages.slice(0, -1), newMessage];
          } else {
            messages = [...existingMessages, newMessage];
          }
        } else {
          messages = existingMessages;
        }

        (event as any).messages = messages;
      }

      next.set(event.sync_id, event);
      console.log(`[useSyncEvents] Updated activeSyncs map, size: ${next.size}`);
      return next;
    });

    // Fire appropriate callback
    switch (event.status) {
      case 'started':
        onSyncStarted?.(event);
        break;
      case 'progress':
        onSyncProgress?.(event);
        break;
      case 'completed':
        onSyncCompleted?.(event);
        // Auto-cleanup after delay
        scheduleCleanup(event.sync_id);
        break;
      case 'failed':
        onSyncFailed?.(event);
        // Auto-cleanup after delay
        scheduleCleanup(event.sync_id);
        break;
      case 'error':
        onSyncFailed?.(event);
        scheduleCleanup(event.sync_id);
        break;
    }
  }, [onSyncStarted, onSyncProgress, onSyncCompleted, onSyncFailed, scheduleCleanup]);

  // Get sync state for a specific sync_id
  const getSyncState = useCallback((syncId: string): SyncEvent | undefined => {
    return activeSyncs.get(syncId);
  }, [activeSyncs]);

  // Check if any sync (or a specific sync) is currently active
  const isSyncing = useCallback((syncId?: string): boolean => {
    if (syncId) {
      const sync = activeSyncs.get(syncId);
      return sync ? sync.status === 'started' || sync.status === 'progress' : false;
    }

    // Check if any sync is active
    for (const sync of activeSyncs.values()) {
      if (sync.status === 'started' || sync.status === 'progress') {
        return true;
      }
    }
    return false;
  }, [activeSyncs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timers
      for (const timer of cleanupTimersRef.current.values()) {
        clearTimeout(timer);
      }
      cleanupTimersRef.current.clear();
    };
  }, []);

  return {
    activeSyncs,
    handleSyncEvent,
    getSyncState,
    isSyncing,
  };
}
